import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb } from "lucide-react";
import AdminShell from "../../components/layout/AdminShell";
import ReceiptDetailDrawer from "../../components/ReceiptDetailDrawer";
import StatusBadge from "../../components/StatusBadge";
import QuotaBanner from "../../components/QuotaBanner";
import UploadPanel from "../../components/UploadPanel";
import { useAuth } from "../../context/AuthContext";
import { ReceiptsApi } from "../../lib/api";
import { formatMoney, relativeTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { useI18n } from "../../i18n";

const TIP_KEYS = ["scan.tip1", "scan.tip2", "scan.tip3"];

const Scan = () => {
  const { t } = useI18n();
  const { currency, categories } = useAuth();
  const [recent, setRecent] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [quota, setQuota] = useState(null);

  const load = useCallback(() => {
    ReceiptsApi.list({ limit: 8 })
      .then((d) => setRecent(d.receipts || []))
      .catch(() => setRecent([]));
    ReceiptsApi.quota()
      .then(setQuota)
      .catch(() => setQuota(null));
  }, []);

  const onParsed = useCallback(() => load(), [load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminShell
      title={t("scan.title")}
      subtitle={t("scan.sub")}
      actions={
        <Link to="/app/receipts">
          <Button variant="outline" size="sm">
            {t("scan.allReceipts")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <QuotaBanner quota={quota} />
          <UploadPanel currency={currency} onParsed={onParsed} quota={quota} />
        </div>

        <div className="space-y-5">
          <div className="ll-card p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Lightbulb className="h-4 w-4 text-primary" /> {t("scan.tipsTitle")}
            </div>
            <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
              {TIP_KEYS.map((key) => (
                <li key={key} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>

          <div className="ll-card overflow-hidden">
            <div className="border-b px-4 py-3">
              <h2 className="font-display text-sm font-semibold">{t("scan.recentTitle")}</h2>
            </div>
            {recent.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">{t("scan.nothingScanned")}</p>
            ) : (
              <ul className="divide-y" data-testid="recent-scans-list">
                {recent.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setOpenId(r.id)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/50"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {r.vendor_name || r.filename || "Receipt"}
                        </div>
                        <div className="text-xs text-muted-foreground">{relativeTime(r.created_at)}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="ll-num text-sm font-semibold">
                          {r.gross_total != null ? formatMoney(r.gross_total, r.currency || currency) : "—"}
                        </div>
                        <StatusBadge status={r.status} />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ReceiptDetailDrawer
        receiptId={openId}
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        onChanged={load}
        isAdmin
        categories={categories}
        currency={currency}
      />
    </AdminShell>
  );
};

export default Scan;
